/**
 * Who you are: a name you typed and a body you picked.
 *
 * This is the whole of character creation and it is meant to stay that way. The
 * game is about what a hotel thinks of you, and the only two things about you
 * it can think anything about are what you are called and whether it can pick
 * you out of a corridor. A slider for shoulder width would be a slider for
 * nothing, because nobody in the building reads it.
 *
 * What creation actually does, mechanically, is nothing at all — and that is
 * the design. You start at zero with all seven departments whichever body you
 * pick, because a preset that started you warm with Kitchen would be a stat,
 * and a stat picked on a screen before the player knows what Kitchen is worth
 * is a trap rather than a choice. The look is a look.
 *
 * `created` is the flag the title screen reads. It is derived from the name
 * rather than stored beside it, so there is no way to be half hired.
 */

import { BODIES, BODY_IDS, bodyFor } from '../data/bodies.js';

/** Long enough for a name, short enough not to break a label. */
export const NAME_LIMIT = 24;

/**
 * Names go into the HUD, the dialogue and the shift summary, all of which build
 * their markup as strings. Stripping the handful of characters that could close
 * a tag is cheaper and more honest than remembering to escape a player-typed
 * string at four call sites, one of which will be added later by somebody who
 * did not read this comment.
 */
export function cleanName(raw) {
  return String(raw ?? '')
    .replace(/[<>&"'`\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_LIMIT);
}

export function createIdentity({ bodies = BODIES, fallback = 'New Hire' } = {}) {
  let name = '';
  let bodyId = bodies[0].id;
  const listeners = new Set();

  function announce() {
    for (const listener of listeners) listener({ name: name || fallback, body: bodyFor(bodyId) });
  }

  return {
    /** Whether anybody has been hired yet. The title screen's only question. */
    get created() {
      return name.length > 0;
    },
    get name() {
      return name || fallback;
    },
    get bodyId() {
      return bodyId;
    },
    get body() {
      return bodyFor(bodyId);
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** The one call the creation screen makes. */
    hire({ name: called, body }) {
      const cleaned = cleanName(called);
      if (cleaned) name = cleaned;
      if (BODY_IDS.includes(body)) bodyId = body;
      announce();
      return { name: this.name, body: this.body, created: this.created };
    },

    toJSON: () => ({ name, body: bodyId }),
    load(saved) {
      name = cleanName(saved?.name);
      bodyId = BODY_IDS.includes(saved?.body) ? saved.body : bodies[0].id;
      announce();
    },
    clear() {
      name = '';
      bodyId = bodies[0].id;
      announce();
    },
  };
}
